import { useEffect, useState } from 'react';
import { debounce } from 'lodash';

const SCROLL_TARGET_ID = 'scrollTarget';
const DEFAULT_DELAY_MS = 200;

let isScrollIdle = true;
let listenerCount = 0;
const subscribers = new Set<(idle: boolean) => void>();

function notifySubscribers() {
  subscribers.forEach((subscriber) => subscriber(isScrollIdle));
}

function attachScrollListener(delay: number) {
  const scrollTarget = document.getElementById(SCROLL_TARGET_ID);

  if (!scrollTarget) {
    return;
  }

  const onScrollEnd = debounce(() => {
    isScrollIdle = true;
    notifySubscribers();
  }, delay);

  const onScroll = () => {
    if (isScrollIdle) {
      isScrollIdle = false;
      notifySubscribers();
    }
    onScrollEnd();
  };

  scrollTarget.addEventListener('scroll', onScroll, { passive: true });

  return () => {
    scrollTarget.removeEventListener('scroll', onScroll);
    onScrollEnd.cancel();
  };
}

let detachScrollListener: (() => void) | undefined;

export function useScrollIdle(delay = DEFAULT_DELAY_MS) {
  const [idle, setIdle] = useState(isScrollIdle);

  useEffect(() => {
    subscribers.add(setIdle);
    listenerCount += 1;

    if (listenerCount === 1) {
      detachScrollListener = attachScrollListener(delay);
    }

    return () => {
      subscribers.delete(setIdle);
      listenerCount -= 1;

      if (listenerCount === 0) {
        detachScrollListener?.();
        detachScrollListener = undefined;
        isScrollIdle = true;
      }
    };
  }, [delay]);

  return idle;
}
